/**
 * Full-page markdown editor for creating and editing markdown documents.
 * Uses CodeMirror with markdown syntax highlighting and auto-save.
 */

import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDocument, useDocumentView } from "../hooks/useDocuments";
import { useProject } from "../hooks/useProjects";
import { useWorkspaceUrl } from "../lib/routes";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useFileUpload } from "../hooks/useFileUpload";
import { updateDocumentMarkdown, updateDocumentName, deleteFile } from "../services/naraDoor";
import { KiiImagePicker } from "@/components/kii/kii-image-picker";
import type { NMPhoto } from "@/components/kii/kii-editor";
import type { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { ArrowLeft, Check, Loader2, Image, PanelRightClose } from "lucide-react";

const CodeMirror = lazy(() => import("@uiw/react-codemirror"));

export function MarkdownEditor() {
  const { t } = useTranslation();
  const { projectId, id: documentId } = useParams<{
    projectId: string;
    id: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const urls = useWorkspaceUrl();
  const { isLoading: workspaceLoading } = useWorkspace();

  const { data: document, isLoading: documentLoading, isFetching: documentFetching } = useDocument(projectId, documentId);
  const { data: documentView, isLoading: viewLoading, isFetching: viewFetching } = useDocumentView(projectId, documentId, document?.status);
  const { data: project } = useProject(projectId);

  const editorRef = useRef<ReactCodeMirrorRef>(null);

  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // A failed autosave or rename stays on screen until the next save succeeds.
  const [saveFailed, setSaveFailed] = useState(false);
  const [photoDeleteFailed, setPhotoDeleteFailed] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const initializedRef = useRef(false);

  // File upload hook for adding images to this document
  const {
    uploadToDocument,
    isUploading,
    fileProgress: uploadProgress,
    error: uploadError,
  } = useFileUpload({
    projectId: projectId || "",
    onComplete: () => {
      queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
    },
  });

  // Image files from the document view
  const imageFiles: NMPhoto[] = useMemo(() => {
    if (!documentView?.image_files) return [];
    return (documentView.image_files as any[]).map((f: any) => ({
      id: f.id,
      filename: f.filename || f.name || "",
      url: f.url,
      mime_type: f.mime_type,
      size: f.size,
      metadata: f.metadata,
    }));
  }, [documentView?.image_files]);

  // CodeMirror modules (lazy-loaded)
  const [cmModules, setCmModules] = useState<{
    md: typeof import("@codemirror/lang-markdown");
    theme: typeof import("@uiw/codemirror-theme-tokyo-night");
    view: typeof import("@codemirror/view");
  } | null>(null);

  useMemo(() => {
    if (!cmModules) {
      Promise.all([
        import("@codemirror/lang-markdown"),
        import("@uiw/codemirror-theme-tokyo-night"),
        import("@codemirror/view"),
      ]).then(([mdMod, themeMod, viewMod]) => {
        setCmModules({ md: mdMod, theme: themeMod, view: viewMod });
      });
    }
  }, []);

  const extensions = useMemo(() => {
    if (!cmModules) return [];
    return [
      cmModules.md.markdown(),
      cmModules.view.EditorView.lineWrapping,
      cmModules.view.EditorView.contentAttributes.of({ style: "max-width: 80ch; margin: 0 auto" }),
    ];
  }, [cmModules]);

  // Drafts start from server data that is not being refetched, never from a stale cached copy.
  // Once started, a draft is never overwritten.
  useEffect(() => {
    if (document?.name && !documentName && !documentFetching) {
      setDocumentName(document.name);
    }
  }, [document, documentFetching]);

  // Initialize markdown content from document
  useEffect(() => {
    if (initializedRef.current || viewFetching || documentFetching) return;
    // Try content_md from documentView first, then from document
    const content = documentView?.content_md ?? (document as any)?.content_md;
    if (content !== undefined) {
      setMarkdownContent(content || "");
      initializedRef.current = true;
    } else if (documentView && document && !documentLoading && !viewLoading) {
      // Document loaded but no content_md — start with empty
      setMarkdownContent("");
      initializedRef.current = true;
    }
  }, [documentView, document, documentLoading, viewLoading, viewFetching, documentFetching]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!projectId || !documentId) throw new Error("Document ID required");
      setIsSaving(true);
      return updateDocumentMarkdown(projectId, documentId, content);
    },
    onSuccess: () => {
      setHasChanges(false);
      setIsSaving(false);
      setSaveFailed(false);
      setLastSaved(new Date());
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
    },
    onError: (error: any) => {
      setIsSaving(false);
      console.error("Failed to save markdown:", error);
      setSaveFailed(true);
    },
  });

  // Auto-save effect
  useEffect(() => {
    if (!hasChanges || markdownContent === null) return;

    const timeoutId = setTimeout(() => {
      saveMutation.mutate(markdownContent);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [markdownContent, hasChanges]);

  // Save document name mutation
  const saveNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!projectId || !documentId) throw new Error("Document ID required");
      setIsSavingName(true);
      return updateDocumentName(projectId, documentId, name);
    },
    onSuccess: () => {
      setIsSavingName(false);
      setSaveFailed(false);
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error: any) => {
      setIsSavingName(false);
      console.error("Failed to save document name:", error);
      setSaveFailed(true);
      if (document?.name) {
        setDocumentName(document.name);
      }
    },
  });

  const handleContentChange = useCallback((value: string) => {
    setMarkdownContent(value);
    setHasChanges(true);
  }, []);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDocumentName(e.target.value);
    },
    []
  );

  const handleNameBlur = useCallback(() => {
    const trimmed = documentName.trim();
    if (trimmed && trimmed !== document?.name) {
      saveNameMutation.mutate(trimmed);
    }
  }, [documentName, document?.name]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        (e.target as HTMLInputElement).blur();
      }
    },
    []
  );

  const handleBack = useCallback(() => {
    if (urls && projectId && documentId) {
      navigate(urls.document(projectId, documentId));
    }
  }, [urls, projectId, documentId, navigate]);

  // Insert image reference at cursor position
  const handleImageSelect = useCallback((photo: NMPhoto) => {
    const view = editorRef.current?.view;
    if (!view) return;

    const filename = photo.filename || "image";
    const text = `![${filename}](nara-file://${photo.id})\n`;

    const { from } = view.state.selection.main;
    view.dispatch({
      changes: { from, insert: text },
      selection: { anchor: from + text.length },
    });
    view.focus();
  }, []);

  // Upload images to document
  const handleImageUpload = useCallback(
    (files: File[]) => {
      if (!documentId) return;
      uploadToDocument(files, documentId);
    },
    [documentId, uploadToDocument],
  );

  // Delete image from document
  const handleImageDelete = useCallback(
    async (photoId: string) => {
      if (!projectId || !documentId) return;
      try {
        await deleteFile(projectId, documentId, photoId);
        setPhotoDeleteFailed(false);
        queryClient.invalidateQueries({ queryKey: ["documentView", documentId] });
      } catch (err) {
        console.error("Failed to delete file:", err);
        setPhotoDeleteFailed(true);
      }
    },
    [projectId, documentId, queryClient],
  );

  // Loading state
  if (workspaceLoading || documentLoading || viewLoading || markdownContent === null) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const saveStatusText = isSaving
    ? t("common.saving", "Saving...")
    : hasChanges
      ? t("common.unsaved", "Unsaved changes")
      : lastSaved
        ? t("common.saved", "Saved")
        : null;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card shrink-0">
        <button
          onClick={handleBack}
          className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors"
          title={t("common.back", "Back")}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <input
          type="text"
          value={documentName}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onKeyDown={handleNameKeyDown}
          className="flex-1 text-lg font-medium bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
          placeholder={t("markdownEditor.untitled", "Untitled Document")}
        />

        {saveStatusText && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : !hasChanges && lastSaved ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : null}
            <span>{saveStatusText}</span>
          </div>
        )}

        {saveFailed && (
          <span role="alert" className="text-xs text-destructive shrink-0">
            {t("documentDetail.saveError")}
          </span>
        )}

        {(uploadError || photoDeleteFailed) && (
          <span role="alert" className="text-xs text-destructive shrink-0">
            {t(uploadError ? "documentDetail.photoUploadError" : "documentDetail.photoDeleteError")}
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1 px-4 py-1.5 border-b border-border bg-card/50 shrink-0">
        <button
          onClick={() => setShowImagePanel((prev) => !prev)}
          className={`p-1.5 rounded-md transition-colors ${
            showImagePanel
              ? "text-primary bg-primary/10"
              : "text-muted-foreground hover:text-foreground hover:bg-accent"
          }`}
          title={t("markdownEditor.toggleImages", "Toggle image panel")}
        >
          {showImagePanel ? (
            <PanelRightClose className="h-4 w-4" />
          ) : (
            <Image className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Editor + Image Panel */}
      <div className="flex-1 min-h-0 flex">
        {/* Editor */}
        <div className="flex-1 min-w-0 overflow-auto [&_.cm-editor]:h-full [&_.cm-scroller]:overflow-auto">
          {cmModules ? (
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <CodeMirror
                ref={editorRef}
                value={markdownContent}
                theme={cmModules.theme.tokyoNight}
                extensions={extensions}
                editable={true}
                onChange={handleContentChange}
                basicSetup={{
                  lineNumbers: false,
                  foldGutter: false,
                  highlightActiveLine: true,
                  highlightSelectionMatches: true,
                }}
                className="h-full text-sm"
              />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Image Panel */}
        {showImagePanel && (
          <div className="w-72 shrink-0 border-l border-border bg-card overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-border shrink-0">
              <h3 className="text-sm font-medium text-foreground">
                {t("markdownEditor.images", "Images")}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("markdownEditor.clickToInsert", "Click an image to insert it")}
              </p>
            </div>
            <div className="flex-1 min-h-0 p-3">
              <KiiImagePicker
                images={imageFiles}
                onSelect={handleImageSelect}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                uploadProgress={uploadProgress}
                isUploading={isUploading}
                preserveAspectRatio
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
